export interface VoiceAction {
  type: string;
  confidence: number;
  data: Record<string, any>;
  description: string;
}

/** Contexte court pour l’API voice-command (page courante). */
export function getVoicePageContext(pathname: string | null | undefined): string {
  if (!pathname) return "dashboard";
  if (pathname.includes("devis")) return "devis";
  if (pathname.includes("factures")) return "factures";
  if (pathname.includes("tiers")) return "tiers";
  if (pathname.includes("produits")) return "produits";
  if (pathname.includes("prospect")) return "prospects";
  if (pathname.includes("crm")) return "crm";
  if (pathname.includes("commandes")) return "commandes";
  if (pathname.includes("chantier")) return "chantiers";
  return "dashboard";
}

export async function parseVoiceCommand(
  transcript: string,
  context: string
): Promise<VoiceAction> {
  const res = await fetch('/api/ai/voice-command', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ transcript, context }),
  });
  return res.json();
}

export async function executeVoiceAction(
  action: VoiceAction,
  router: { push: (url: string) => void }
): Promise<string> {
  switch (action.type) {
    case 'create_prospect': {
      router.push("/crm/prospects");
      return `Pipeline prospects ouvert — utilisez l’assistant (micro) pour préremplir un prospect.`;
    }

    case 'create_devis': {
      const params = new URLSearchParams();
      const client = action.data.client || action.data.societe || "";
      const sujet =
        action.data.sujet || action.data.intitule || action.data.titre || "";
      const notes =
        action.data.besoin ||
        action.data.description ||
        action.data.lignes ||
        action.data.detail ||
        "";
      if (client) params.set("client", String(client));
      if (sujet) params.set("sujet", String(sujet));
      if (notes) params.set("notes", String(notes));
      const q = params.toString();
      router.push(q ? `/devis/nouveau?${q}` : "/devis/nouveau");
      return `Nouveau devis${client ? ` — client : ${client}` : ""}`;
    }

    case 'validate_devis':
      if (action.data.devis_ref) {
        await fetch(`/api/dolibarr/proposals/${action.data.devis_id}/validate`, {
          method: 'POST',
        });
        return `Devis ${action.data.devis_ref} validé`;
      }
      return 'Référence du devis non trouvée';

    case 'sign_devis':
      if (action.data.devis_id) {
        await fetch(`/api/dolibarr/proposals/${action.data.devis_id}/close`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            status: 6,
            note_private: 'Signé par commande vocale',
          }),
        });
        return `Devis ${action.data.devis_ref} signé`;
      }
      return 'Devis non trouvé';

    case 'create_action':
      await fetch('/api/dolibarr/agendaevents', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          label: action.data.label,
          type_code: action.data.type_code || 'AC_TEL',
          datep:
            action.data.date || new Date().toISOString().split('T')[0],
          socid: action.data.socid || '',
          note: action.data.note || '',
          userownerid: 1,
        }),
      });
      return `Action "${action.data.label}" créée`;

    case 'relance':
      router.push(
        `/crm/actions?new=1&type=AC_TEL&client=${encodeURIComponent(action.data.client || '')}`
      );
      return `Relance créée pour ${action.data.client || 'le client'}`;

    case 'search':
      router.push(`/recherche?q=${encodeURIComponent(action.data.query)}`);
      return `Recherche : ${action.data.query}`;

    case 'navigate': {
      const routes: Record<string, string> = {
        dashboard: '/',
        devis: '/devis',
        factures: '/factures',
        tiers: '/tiers',
        clients: '/tiers?type=client',
        prospects: '/crm/prospects',
        produits: '/produits',
        commandes: '/commandes-fournisseurs',
        chantiers: '/chantiers',
        parametres: '/parametres',
        actions: '/crm/actions',
        calendrier: '/calendrier',
      };
      const page = action.data.page?.toLowerCase();
      const route = routes[page] || '/';
      router.push(route);
      return `Navigation vers ${page}`;
    }

    case 'question':
      return action.data.answer || 'Je traite votre question...';

    case 'add_line':
      return `Ajout de ${action.data.qty || 1}× ${action.data.description || action.data.product || ''}`;

    default:
      return 'Action non reconnue';
  }
}
